// Stripe.js from Stripe's own host, as Stripe requires for card fields. The
// card number, expiry and security code stay in Stripe iframes, so they
// never reach CMP. One script per page; one Stripe object per account.

export type StripeCardElement = {
  mount: (element: HTMLElement) => void;
  destroy: () => void;
  on: (event: 'change', handler: (event: { complete: boolean; error?: { message: string } }) => void) => void;
};
export type StripeElements = { create: (type: 'cardNumber' | 'cardExpiry' | 'cardCvc', options?: Record<string, unknown>) => StripeCardElement };
/** Stripe Custom Checkout card form (the staff Process Payment form; MD-I1). */
export type StripePaymentElement = {
  mount: (element: HTMLElement) => void;
  destroy: () => void;
  on: (event: 'change' | 'ready', handler: (event: { complete?: boolean }) => void) => void;
};
export type StripeCheckoutActions = {
  confirm: (options?: { redirect?: 'if_required' | 'always' }) =>
    Promise<{ type: 'success' } | { type: 'error'; error: { message?: string } }>;
  getSession: () => { total?: { total?: { amount?: string; minorUnitsAmount?: number } } };
};
export type StripeCheckoutElementsSdk = {
  createPaymentElement: (options?: Record<string, unknown>) => StripePaymentElement;
  loadActions: () => Promise<{ type: 'success'; actions: StripeCheckoutActions } | { type: 'error'; error: { message?: string } }>;
};
export type StripeClient = {
  initCheckoutElementsSdk: (options: { clientSecret: string; elementsOptions?: Record<string, unknown> }) => StripeCheckoutElementsSdk;
  elements: () => StripeElements;
  confirmCardSetup: (clientSecret: string, data: Record<string, unknown>) =>
    Promise<{ error?: { message?: string }; setupIntent?: { id: string; status: string } }>;
};
type StripeFactory = (publishableKey: string, options: { stripeAccount: string }) => StripeClient;

// The versioned release the staff dashboard loads (@stripe/stripe-js RELEASE_TRAIN); the
// card form needs Basil or later, and card fields work in it too.
const STRIPE_JS_URL = 'https://js.stripe.com/dahlia/stripe.js';
let scriptPromise: Promise<StripeFactory> | null = null;
const clients = new Map<string, StripeClient>();

const loadStripeFactory = (): Promise<StripeFactory> => {
  const existing = (window as unknown as { Stripe?: StripeFactory }).Stripe;
  if (existing) return Promise.resolve(existing);
  if (!scriptPromise) {
    scriptPromise = new Promise<StripeFactory>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = STRIPE_JS_URL;
      script.async = true;
      script.onload = () => {
        const factory = (window as unknown as { Stripe?: StripeFactory }).Stripe;
        if (factory) resolve(factory); else reject(new Error('Secure card fields could not load. Refresh the page and try again.'));
      };
      script.onerror = () => { scriptPromise = null; reject(new Error('Secure card fields could not load. Check your connection, then try again.')); };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

export const getStripeClient = async (publishableKey: string, connectedAccountId: string): Promise<StripeClient> => {
  const key = `${publishableKey}:${connectedAccountId}`;
  const cached = clients.get(key);
  if (cached) return cached;
  const client = (await loadStripeFactory())(publishableKey, { stripeAccount: connectedAccountId });
  clients.set(key, client);
  return client;
};
